import { useCallback, useEffect, useState, useRef } from 'react';
import {
  Outlet,
  useParams,
  generatePath,
  useMatch,
  useNavigate,
  NavLink,
  useOutletContext,
  useLocation,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  Tabs,
  IconLoader,
  IconArrowLeft,
  Account,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useGetCustomersInfoDictionariesQuery } from '../../store/dictionaries/services';
import { clearGeneralState, setBalances } from './store/slices/general-info';
import {
  useLazyGetCorporateUserQuery,
  useLazyGetCorporateAccountBalanceQuery,
  useLazyGetCorporateMainChannelQuery,
  useLazyGetCorporatelUserScoreQuery,
} from './store/services/corporate';
import { useLazyGetCustomersTopUpQuery } from '../customers/store/services/customers';
import { clearOverviewState } from './store/slices/overview/overview';
import { clearAccountsState } from './store/slices/accounts/accounts';
import { clearAmlState } from './store/slices/aml/aml';
import { clearStatisticState } from '../customers/store/slices/statistic';
import { clearExternalAccounts } from '../customers/store/slices/external-accounts';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../customers/store/slices/statements';
import { clearBeneficiariesState } from '../customers/store/slices/beneficiaries/beneficiaries';
import { clearTariffsState } from '../customers/store/slices/tariffs/tariff';
import { clearDocumentsState } from '../customers/store/slices/documents/documents';
import { CustomerType, Balance, CustomerScore } from '../customers/types';
import CustomerRightSide from '../../components/partial/info-right-side/info-right-side';
import CorporateRightSideContent from '../../components/corporate/right-side-content';

import ROUTES from '../../routes';

import cn from '../individual/information.module.css';

const CorporateCustomerInformation = () => {
  const { customerId } = useParams();
  const {
    corporateCustomers: {
      info: {
        general: { baseInformation, totalBalance, topUpOptions, mainChat },
        overview: { companyOfficial },
        aml: { aml },
      },
    },
    customersDictionaries: { cardsEnabled },
  } = useAppSelector((state) => state);

  const { activePermissions } = useAppSelector((state) => state.root);
  const [notFound, setNotFound] = useState(false);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [withOverflow, setWithOverflow] = useState(false);
  const [hiddenRoutes, setHiddenRoutes] = useState<
    { name: string; path: string; hidden?: boolean }[]
  >([]);
  const [tabs, setTabs] = useState<
    { name: string; path: string; hidden?: boolean }[]
  >([
    {
      name: 'Overview',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Accounts',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'External Integration',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
      ),
    },
    {
      name: 'Cards',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_CARDS, {
        customerId: customerId as string,
      }),
      hidden: !cardsEnabled,
    },
    {
      name: 'AML & KYC',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_AML, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Tariff',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_TARIFFS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Documents',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ),
    },
    {
      name: 'Statistics',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_STATISTICS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Beneficiaries',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST),
    },
  ]);

  const defaultTabs = [
    {
      name: 'Overview',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Accounts',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'External Integration',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
      ),
    },
    {
      name: 'Cards',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_CARDS, {
        customerId: customerId as string,
      }),
      hidden: !cardsEnabled,
    },
    {
      name: 'AML & KYC',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_AML, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Tariff',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_TARIFFS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Documents',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ),
    },
    {
      name: 'Statistics',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_STATISTICS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Beneficiaries',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST),
    },
  ];

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { search } = useLocation();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const match = useMatch(ROUTES.CUSTOMERS_CORPORATE_PAGE);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [getCorporateUser, { isLoading, isFetching }] =
    useLazyGetCorporateUserQuery();
  const [getBalance] = useLazyGetCorporateAccountBalanceQuery();
  const [getCustomersTopUp] = useLazyGetCustomersTopUpQuery();
  const [getCorporateMainChannel] = useLazyGetCorporateMainChannelQuery();
  const [getCorporateScore] = useLazyGetCorporatelUserScoreQuery();

  useGetCustomersInfoDictionariesQuery();

  const getBalancesAction = useCallback(async (accounts?: Account[]) => {
    if (accounts && accounts.length) {
      const balancesResponse = await Promise.all(
        accounts.map(async (item) => {
          const response = await getBalance(item.id);

          return response?.data?.data;
        }),
      );

      const formatted: Balance[] = [];

      balancesResponse.forEach((item) => {
        item?.edges.forEach((balance) => {
          formatted.push(balance.node);
        });
      });

      dispatch(
        setBalances({
          balances: formatted,
          totalBCBalances: (accounts
            ? accounts.map((item) => item.totalBalanceBC)
            : [0]) as number[],
        }),
      );
    }
  }, []);

  const getUserAction = useCallback(async () => {
    const { error } = (await getCorporateUser(customerId as string)) as {
      data: any;
      error: any;
    };
    const amlUpdateId = localStorage.getItem('amlCorporateUpdateId');

    if (error?.data) {
      if (error.data.statusCode === 400) {
        setNotFound(true);
      }
    } else if (
      !error &&
      !amlUpdateId &&
      activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST)
    ) {
      (await getCorporateScore(customerId as string)) as {
        data: { data: CustomerScore };
      };
    }
  }, []);

  const onClear = () => {
    dispatch(clearGeneralState());
    dispatch(clearOverviewState());
    dispatch(clearAccountsState());
    dispatch(clearAmlState());
    dispatch(clearStatisticState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearExternalAccounts());
    dispatch(clearBeneficiariesState());
  };

  useEffect(() => {
    if (customerId) {
      getUserAction();
      getCorporateMainChannel(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (query === undefined) {
      setQuery(search);
    }
  }, [search, query]);

  useEffect(() => {
    if (baseInformation) {
      getBalancesAction(baseInformation?.accounts);

      if (!topUpOptions) {
        getCustomersTopUp({});
      }
    }
  }, [baseInformation]);

  useEffect(() => {
    if (match && customerId) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW, { customerId }),
      );
    }
  }, [match, customerId]);

  useEffect(() => {
    if (
      tabsWrapperRef &&
      tabsWrapperRef.current &&
      tabsRef &&
      tabsRef.current &&
      baseInformation &&
      !isLoading
    ) {
      if (
        tabsRef?.current?.scrollWidth > tabsWrapperRef?.current?.offsetWidth
      ) {
        setHiddenRoutes(defaultTabs.slice(-3));
        setTabs(defaultTabs.slice(0, -3));
        setWithOverflow(false);
      } else {
        setHiddenRoutes([]);
        setTabs(defaultTabs);
        setWithOverflow(true);
      }
    }
  }, [tabsWrapperRef, tabsRef, baseInformation, isLoading]);

  useEffect(
    () => () => {
      onClear();
    },
    [dispatch],
  );

  useEffect(() => {
    let title = baseInformation?.name || 'Corporate Customers';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.name]);

  let content;

  if (isLoading || (isFetching && !baseInformation)) {
    content = (
      <div className={cn.individualCustomerLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && notFound) {
    content = (
      <div className={cn.userNotFound}>
        <div className={cn.userNotFoundTop}>
          <NavLink
            to={`${ROUTES.CUSTOMERS_CORPORATE_LIST}${query || ''}`}
            className={cn.backLink}
            onClick={() => onClear()}
          >
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
        </div>
        <div
          className={cn.userNotFoundContent}
        >{`Client with identifier ${customerId} not found`}</div>
      </div>
    );
  } else if (
    !isLoading &&
    activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_LIST) &&
    !notFound
  ) {
    content = (
      <>
        <div className={cn.individualCustomerLeft} ref={wrapperRef}>
          <div className={cn.linkWrapper}>
            <NavLink
              to={`${ROUTES.CUSTOMERS_CORPORATE_LIST}${query || ''}`}
              className={cn.backLink}
              onClick={() => onClear()}
            >
              <IconArrowLeft width={13} height={11} />
              Back
            </NavLink>
          </div>
          <div className={cn.individualCustomerTabs} ref={tabsWrapperRef}>
            <Tabs
              tabs={tabs}
              hiddenRoutes={hiddenRoutes}
              itemClassName={cn.customerTab}
              wrapperClassName={`${cn.customerTabs} ${withOverflow ? cn.tabsWithOverflow : ''}`}
              wrapperRef={tabsRef}
              enableRouting
            />
          </div>
          <Outlet context={{ sendMessage, wrapperRef }} />
        </div>
        <div className={cn.individualCustomerRight}>
          <CustomerRightSide
            customerId={customerId as string}
            risk={aml?.totalRiskScore}
            balance={totalBalance?.balance as number}
            tariff={baseInformation?.tariff as string}
            customerType={CustomerType.CORPORATE}
            name={baseInformation?.name}
            mainChat={mainChat}
            status={baseInformation?.status}
          >
            <CorporateRightSideContent
              name={baseInformation?.name as string}
              registrationNumber={baseInformation?.registrationNumber as string}
              owner={companyOfficial?.find(
                (item) =>
                  item.companyOfficialType === 'MAIN_REPRESENTATIVE' &&
                  item.status === 'APPROVED',
              )}
              sic={baseInformation?.sic as string}
              countryOfIncorporated={
                baseInformation?.countryOfIncorporated as string
              }
            />
          </CustomerRightSide>
        </div>
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return <div className={cn.individualCustomer}>{content}</div>;
};

export default CorporateCustomerInformation;
